import React, { useState, useEffect, useMemo } from 'react';
import {
    ChevronLeft,
    ChevronRight,
    Calendar as CalendarIcon,
    Edit3,
    Save,
    CheckCircle,
    XCircle,
    AlertCircle,
    Clock
} from 'lucide-react';
import { collection, getDocs, doc, setDoc, getDoc, addDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useToast } from '../../contexts/ToastContext';
import { SchoolTime } from '../../types/schedule';
import { getSchoolTimes } from '../../utils/schedule';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Modal from '../ui/Modal';

interface CalendarEditorProps {
    onBack: () => void;
}

type DayStatus = 'normal' | 'holiday' | null;

interface CalendarDay {
    date: Date;
    day: number;
    isHoliday: boolean;
    isFuture: boolean;
    isToday: boolean;
    isSchoolHoliday: boolean; // Based on school time config
    isCustomHoliday: boolean; // Admin overridden holiday
    isWorkingHoliday: boolean; // Holiday but marked as working day
}

const CalendarEditor: React.FC<CalendarEditorProps> = ({ onBack }) => {
    const { showSuccess, showError } = useToast();

    // Calendar state
    const [currentDate, setCurrentDate] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState<DayStatus>(null);
    const [saving, setSaving] = useState(false);

    // School time and calendar data
    const [schoolTimes, setSchoolTimes] = useState<SchoolTime[]>([]);
    const [calendarConfig, setCalendarConfig] = useState<Record<string, any>>({});
    const [loading, setLoading] = useState(false);

    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();

    // Check if a day is a holiday based on school time configuration
    const isHolidayFromSchoolConfig = (date: Date): boolean => {
        if (schoolTimes.length === 0) return false;

        const activeSchoolTime = schoolTimes.find(st => st.isActive);
        if (!activeSchoolTime) return false;

        const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
        const dayConfig = activeSchoolTime.days.find(day => day.day === dayName);

        return dayConfig ? !dayConfig.isActive : false;
    };

    // Generate calendar days with holiday information
    const calendarDays = useMemo(() => {
        const year = currentYear;
        const month = currentMonth;

        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const daysInMonth = lastDay.getDate();
        const startingDayOfWeek = firstDay.getDay();
        const today = new Date();

        const days = [];

        // Add empty cells for days before the first day of the month
        for (let i = 0; i < startingDayOfWeek; i++) {
            days.push(null);
        }

        // Add days of the month
        for (let day = 1; day <= daysInMonth; day++) {
            const date = new Date(year, month, day);
            const isFuture = date > today;
            const isToday = date.toDateString() === today.toDateString();
            const isSchoolHoliday = isHolidayFromSchoolConfig(date);

            // Use day number as key (not date string) to match how we save it
            const dayStr = day.toString();
            const dayConfig = calendarConfig[dayStr] || {};
            const isCustomHoliday = dayConfig.isHoliday === true;
            const isWorkingHoliday = dayConfig.isWorkingHoliday === true;

            days.push({
                date,
                day,
                isHoliday: isSchoolHoliday || isCustomHoliday,
                isFuture,
                isToday,
                isSchoolHoliday,
                isCustomHoliday,
                isWorkingHoliday
            });
        }

        return days;
    }, [currentYear, currentMonth, calendarConfig, schoolTimes]);

    useEffect(() => {
        fetchSchoolTimes();
    }, []);

    useEffect(() => {
        fetchCalendarConfig();
    }, [currentYear, currentMonth]);

    const fetchSchoolTimes = async () => {
        try {
            const data = await getSchoolTimes();
            setSchoolTimes(data);
        } catch (error) {
            console.error('Error fetching school times:', error);
            showError('Error', 'Failed to fetch school time configuration');
        }
    };

    const fetchCalendarConfig = async () => {
        setLoading(true);
        try {
            const year = currentYear.toString();
            const monthName = getMonthName(currentMonth);
            const configRef = doc(db, 'calendarConfig', `${year}_${monthName}`);
            const configSnap = await getDoc(configRef);

            if (configSnap.exists()) {
                setCalendarConfig(configSnap.data());
            } else {
                setCalendarConfig({});
            }
        } catch (error) {
            console.error('Error fetching calendar config:', error);
            showError('Error', 'Failed to fetch calendar configuration');
        } finally {
            setLoading(false);
        }
    };

    const navigateMonth = (direction: 'prev' | 'next') => {
        const newDate = new Date(currentDate);
        if (direction === 'prev') {
            newDate.setMonth(newDate.getMonth() - 1);
        } else {
            newDate.setMonth(newDate.getMonth() + 1);
        }
        setCurrentDate(newDate);
    };

    const handleDateClick = (day: CalendarDay) => {
        if (day.isFuture) {
            showError('Error', 'Cannot edit future dates');
            return;
        }

        setSelectedDate(day.date);
        setSelectedStatus(day.isCustomHoliday ? 'holiday' : 'normal');
        setShowStatusModal(true);
    };

    const handleStatusSave = async () => {
        if (!selectedStatus || !selectedDate) return;

        setSaving(true);
        try {
            const year = currentYear.toString();
            const monthName = getMonthName(currentMonth);
            const configRef = doc(db, 'calendarConfig', `${year}_${monthName}`);
            const configSnap = await getDoc(configRef);

            let monthConfig: any = {};
            if (configSnap.exists()) {
                monthConfig = configSnap.data();
            }

            const dayStr = selectedDate.getDate().toString();

            if (selectedStatus === 'holiday') {
                monthConfig[dayStr] = {
                    isHoliday: true,
                    isWorkingHoliday: false,
                    updatedAt: new Date()
                };
            } else {
                // Remove holiday marking
                if (monthConfig[dayStr]) {
                    delete monthConfig[dayStr];
                }
            }

            await setDoc(configRef, monthConfig, { merge: true });

            const message = selectedStatus === 'holiday'
                ? 'Day marked as holiday successfully!'
                : 'Holiday marking removed successfully!';

            showSuccess('Success', message);
            setShowStatusModal(false);
            setSelectedDate(null);
            setSelectedStatus(null);
            fetchCalendarConfig();
        } catch (error) {
            console.error('Error saving calendar config:', error);
            showError('Error', 'Failed to save calendar configuration');
        } finally {
            setSaving(false);
        }
    };

    const getMonthName = (month: number) => {
        const months = [
            'January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'
        ];
        return months[month];
    };

    const getDayStatusIcon = (day: CalendarDay) => {
        if (day.isWorkingHoliday) {
            return <CheckCircle className="h-4 w-4 text-green-600" />;
        }
        if (day.isHoliday) {
            return <span className="text-orange-600 font-bold text-sm">H</span>;
        }
        return null;
    };

    const getDayStatusColor = (day: CalendarDay) => {
        if (day.isWorkingHoliday) {
            return 'bg-green-100 border-green-300';
        }
        if (day.isHoliday) {
            return 'bg-orange-100 border-orange-300';
        }
        return 'bg-white border-gray-200';
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
                <div className="flex items-center space-x-3">
                    <div className="p-2 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 shadow-lg">
                        <CalendarIcon className="h-6 w-6 text-white" />
                    </div>
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900">Calendar Editor</h2>
                        <p className="text-gray-600">Edit calendar holidays and working days</p>
                    </div>
                </div>
                <Button variant="secondary" onClick={onBack}>
                    Back
                </Button>
            </div>

            {/* Month Navigation */}
            <Card glassy={true} className="islamic-pattern">
                <div className="p-6">
                    <div className="flex items-center justify-between mb-6">
                        <Button
                            variant="secondary"
                            onClick={() => navigateMonth('prev')}
                            className="p-2"
                        >
                            <ChevronLeft className="h-5 w-5" />
                        </Button>

                        <div className="text-center">
                            <h3 className="text-xl font-semibold text-gray-900">
                                {getMonthName(currentMonth)} {currentYear}
                            </h3>
                        </div>

                        <Button
                            variant="secondary"
                            onClick={() => navigateMonth('next')}
                            className="p-2"
                        >
                            <ChevronRight className="h-5 w-5" />
                        </Button>
                    </div>

                    {/* Calendar Grid */}
                    <div className="grid grid-cols-7 gap-1">
                        {/* Day Headers */}
                        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                            <div key={day} className="p-2 text-center text-sm font-medium text-gray-500">
                                {day}
                            </div>
                        ))}

                        {/* Calendar Days */}
                        {calendarDays.map((day, index) => {
                            if (!day) {
                                return <div key={index} className="p-2"></div>;
                            }

                            return (
                                <div
                                    key={day.date.toDateString()}
                                    className={`
                                        p-2 min-h-[60px] border rounded-lg cursor-pointer transition-all duration-200
                                        ${day.isFuture
                                            ? 'bg-gray-100 border-gray-200 cursor-not-allowed opacity-50'
                                            : 'hover:bg-gray-50 hover:border-emerald-300'
                                        }
                                        ${day.isToday ? 'ring-2 ring-emerald-500' : ''}
                                        ${getDayStatusColor(day)}
                                    `}
                                    onClick={() => handleDateClick(day)}
                                >
                                    <div className="flex flex-col items-center space-y-1">
                                        <span className={`text-sm font-medium ${day.isFuture ? 'text-gray-400' : 'text-gray-900'
                                            }`}>
                                            {day.day}
                                        </span>

                                        {getDayStatusIcon(day) && (
                                            <div className="flex items-center justify-center w-6 h-6 rounded-full">
                                                {getDayStatusIcon(day)}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </Card>

            {/* Legend */}
            <Card glassy={true} className="islamic-pattern">
                <div className="p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Legend & Instructions</h3>

                    {/* Status Legend */}
                    <div className="mb-4">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Day Types:</h4>
                        <div className="flex flex-wrap gap-4">
                            <div className="flex items-center space-x-2">
                                <div className="w-6 h-6 rounded border border-gray-200 bg-white"></div>
                                <span className="text-sm text-gray-600">Normal Day</span>
                            </div>
                            <div className="flex items-center space-x-2">
                                <div className="w-6 h-6 rounded border border-orange-300 bg-orange-100 flex items-center justify-center">
                                    <span className="text-orange-600 font-bold text-xs">H</span>
                                </div>
                                <span className="text-sm text-gray-600">Holiday (School Config)</span>
                            </div>
                            <div className="flex items-center space-x-2">
                                <div className="w-6 h-6 rounded border border-orange-300 bg-orange-100 flex items-center justify-center">
                                    <span className="text-orange-600 font-bold text-xs">H</span>
                                </div>
                                <span className="text-sm text-gray-600">Custom Holiday (Admin)</span>
                            </div>
                            <div className="flex items-center space-x-2">
                                <div className="w-6 h-6 rounded border border-green-300 bg-green-100 flex items-center justify-center">
                                    <CheckCircle className="h-4 w-4 text-green-600" />
                                </div>
                                <span className="text-sm text-gray-600">Working Holiday</span>
                            </div>
                        </div>
                    </div>

                    {/* Instructions */}
                    <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-2">How to use:</h4>
                        <div className="space-y-1 text-sm text-gray-600">
                            <p>• <strong>Click any date</strong> to mark it as a custom holiday</p>
                            <p>• <strong>Click holiday dates</strong> to remove holiday marking</p>
                            <p>• <strong>Future dates</strong> cannot be edited</p>
                            <p>• <strong>School holidays</strong> are automatically determined from school time configuration</p>
                            <p>• <strong>Custom holidays</strong> override normal days but can be removed</p>
                        </div>
                    </div>
                </div>
            </Card>

            {/* Status Selection Modal */}
            {showStatusModal && selectedDate && (
                <Modal
                    isOpen={true}
                    onClose={() => {
                        setShowStatusModal(false);
                        setSelectedDate(null);
                        setSelectedStatus(null);
                    }}
                    title={`Edit Calendar - ${selectedDate.toLocaleDateString('en-US', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                    })}`}
                    size="sm"
                >
                    <div className="space-y-6">
                        <div className="text-center">
                            <h3 className="text-lg font-medium text-gray-900 mb-2">
                                Select Day Type
                            </h3>
                            <p className="text-sm text-gray-600">
                                {selectedDate.toLocaleDateString('en-US', {
                                    weekday: 'long',
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric'
                                })}
                            </p>
                        </div>

                        <div className="space-y-3">
                            {[
                                {
                                    value: 'normal',
                                    label: 'Normal Day',
                                    icon: Clock,
                                    color: 'text-gray-600',
                                    description: 'Regular working day'
                                },
                                {
                                    value: 'holiday',
                                    label: 'Custom Holiday',
                                    icon: XCircle,
                                    color: 'text-orange-600',
                                    description: 'Mark as holiday (admin override)'
                                }
                            ].map((status) => {
                                const Icon = status.icon;
                                const isSelected = selectedStatus === status.value;

                                return (
                                    <button
                                        key={status.value}
                                        onClick={() => setSelectedStatus(status.value as DayStatus)}
                                        className={`w-full p-4 border-2 rounded-lg transition-all duration-200 text-left ${isSelected
                                            ? 'border-emerald-500 bg-emerald-50'
                                            : 'border-gray-200 hover:border-gray-300'
                                            }`}
                                    >
                                        <div className="flex items-center space-x-3">
                                            <Icon className={`h-6 w-6 ${status.color}`} />
                                            <div>
                                                <span className="font-medium text-gray-900">{status.label}</span>
                                                <p className="text-sm text-gray-600">{status.description}</p>
                                            </div>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>

                        <div className="flex justify-end space-x-3 pt-4">
                            <Button
                                variant="secondary"
                                onClick={() => setShowStatusModal(false)}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleStatusSave}
                                disabled={!selectedStatus}
                                loading={saving}
                            >
                                {saving ? 'Saving...' : 'Save Changes'}
                            </Button>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default CalendarEditor;
